/**
 * Die KSB-Schaltfläche (Kopie aus Axis): Pille, Outline oder Text, drei Größen, positiv auf hellen und negativ auf dunklen
 * Flächen. Gestaltet in `components.css` (`.ksb-btn*`), hier nur die Klassen und das Hover-Icon.
 *
 * **Grün ist hier kein Zustand der Schaltfläche, sondern die Rückmeldung beim Drücken.** Die Pille bleibt Schwarzwald
 * (auf Dunkel: Linde) und füllt sich erst gedrückt mit Grün — so steht Grün in der ganzen App für „wird ausgelöst" und
 * nie für eine Sorte Schaltfläche. Eine zweite Pille neben der ersten gibt es nicht: ein Paar besteht aus Pille und
 * Outline oder Text.
 *
 * Das Icon (Phosphor „Plant", Light, wie im Paket) erscheint erst bei Hover und Druck. `hoverIcon` schaltet es ein.
 * Für Links bietet `LinkButton` dieselbe Optik auf einem Router-`Link`.
 */

import { Plant } from '../icons';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';

import { buttonClassName } from './buttonClassName';
import type { ButtonLookProps } from './buttonClassName';

function HoverIcon() {
  return (
    <span className="ksb-btn__icon" aria-hidden="true">
      <Plant weight="light" size="100%" />
    </span>
  );
}

export interface ButtonProps
  extends ButtonLookProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  className?: string;
  ref?: Ref<HTMLButtonElement> | undefined;
  children: ReactNode;
}

export function Button({
  variant,
  size,
  contrast,
  danger,
  hoverIcon = false,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClassName({ variant, size, contrast, danger }, className)}
      {...rest}
    >
      {children}
      {hoverIcon && <HoverIcon />}
    </button>
  );
}

export interface LinkButtonProps extends ButtonLookProps, Omit<LinkProps, 'className'> {
  className?: string;
}

/** Router-`Link` in Schaltflächen-Optik: ein echter Anker, Mittelklick und „in neuem Tab" funktionieren. */
export function LinkButton({
  variant,
  size,
  contrast,
  danger,
  hoverIcon = false,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link className={buttonClassName({ variant, size, contrast, danger }, className)} {...rest}>
      {children}
      {hoverIcon && <HoverIcon />}
    </Link>
  );
}

export interface AnchorButtonProps
  extends ButtonLookProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> {
  className?: string;
}

/** Externer Link in Schaltflächen-Optik (kein Router). */
export function AnchorButton({
  variant,
  size,
  contrast,
  danger,
  hoverIcon = false,
  className,
  children,
  ...rest
}: AnchorButtonProps) {
  return (
    <a className={buttonClassName({ variant, size, contrast, danger }, className)} {...rest}>
      {children}
      {hoverIcon && <HoverIcon />}
    </a>
  );
}
